import React from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { flattenToAppURL, getBaseUrl } from '@plone/volto/helpers/Url/Url';

// What may appear as a CHILD tab: a Document (Publications, Downloads,
// Persons, ...) or a nested Topic. Images, Files and everything else a page
// may hold as a child are not navigation targets. The section root itself is
// deliberately not checked against this list - it is whichever content type
// owns the section.
const TAB_TYPES = ['Document', 'topic'];

const normalizePath = (path = '') => path.replace(/\/+$/, '') || '/';

/**
 * The section root as configured on the block: the value of its Path
 * criterion. Only accepted when the page being viewed actually sits at or
 * below it, so a stale criterion left over from copying a page cannot point
 * the tab bar at an unrelated part of the site. Volto also lets the Path
 * setting hold a UID rather than a path, which is not resolvable here - the
 * leading-slash test rejects that case and the caller falls back.
 */
const configuredRoot = (data, currentPath) => {
  const raw = (data?.querystring?.query || []).find(
    (criterion) =>
      criterion.i === 'path' &&
      typeof criterion.v === 'string' &&
      criterion.v.startsWith('/'),
  )?.v;
  if (!raw) return null;
  const root = normalizePath(raw);
  return currentPath === root || currentPath.startsWith(`${root}/`)
    ? root
    : null;
};

const labelFromPath = (path) => {
  const segment = path.replace(/\/+$/, '').split('/').pop() || '';
  return segment
    .split('-')
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(' ');
};

/**
 * Generic in-page sub-navigation ("tabs") for a page and its own sub-pages
 * (e.g. a Topic and its "Publications"/"Downloads" children). Not tied to
 * any content type - works anywhere the block's querystring is a plain
 * Path criterion pointing at the page that should show the tab bar (the
 * same config placed as the first block on that page and on each of its
 * sub-pages).
 *
 * Two backend quirks this works around, both specific to plone.restapi's
 * @querystring-search (not general Plone behavior):
 * - The "depth" setting on a Path criterion isn't enforced - it always
 *   returns the whole subtree - so this component does its own depth
 *   filtering (keeps only items exactly one path segment below the
 *   page/topic root, using path length as a proxy for depth).
 * - The endpoint unconditionally excludes the CURRENT page from its own
 *   results (plone/restapi's querystringsearch/get.py adds a hardcoded
 *   `UID: {"not": context.UID()}`, with no opt-out). So the page currently
 *   being viewed is always missing from `items` - this component adds the
 *   current path back in via `location.pathname`, and since it therefore
 *   has no title/data for it, derives a display label from the URL slug
 *   (fine for slugs that are already words, like "publications").
 *
 * `location.pathname` is run through `getBaseUrl` before use, which strips
 * Volto's own non-content route suffixes (`/edit`, `/add`, ...) - without
 * it, editing the page itself made `currentPath` end in "/edit", which is
 * SHORTER than "/downloads" or "/publications" but longer than the real
 * page path, throwing off both which item is "shortest" (Overview) and the
 * depth filter below it - the block silently rendered nothing in the
 * editor because of this (correct once published, since the live route has
 * no such suffix).
 */
const SectionTabsListingView = ({ items, data }) => {
  const location = useLocation();

  const currentPath = normalizePath(
    flattenToAppURL(getBaseUrl(location.pathname)),
  );
  const itemsByPath = new Map(
    (items || []).map((item) => [
      normalizePath(flattenToAppURL(item['@id'])),
      item,
    ]),
  );

  const knownPaths = Array.from(new Set([currentPath, ...itemsByPath.keys()]));

  // The section root comes from the block's own Path criterion, which names
  // it directly. Deriving it from the results instead does not work in
  // general: many of these blocks also carry a portal_type criterion, and
  // that criterion filters out the root itself whenever the section is owned
  // by something other than a Document or Topic - a Project or a Person -
  // leaving their sub-pages with no way back to the overview.
  const overviewPath = normalizePath(
    configuredRoot(data, currentPath) ??
      // No usable criterion (hand-built block, UID-based path setting):
      // fall back to the shortest path in play, since every sub-page's path
      // is the root's path plus one "/segment".
      knownPaths.reduce((a, b) => (a.length <= b.length ? a : b)),
  );
  const overviewDepth = overviewPath.split('/').length;

  const isSubPage = (path) =>
    path !== overviewPath &&
    // Ignore anything deeper than a direct child (e.g. images on a
    // sub-page) - see the depth-enforcement note above.
    path.split('/').length === overviewDepth + 1 &&
    // Only real sub-pages become tabs. Without this the block depends on
    // the editor having added a portal_type criterion to its querystring,
    // and a page that keeps its pictures as children (igus Humanoid Open
    // Platform, CENTAURO, ...) got a tab bar full of photo captions. The
    // current page is never in `items` and so has no @type to test - at the
    // right depth it is a sub-page by construction.
    (itemsByPath.has(path)
      ? TAB_TYPES.includes(itemsByPath.get(path)['@type'])
      : path === currentPath);

  const others = knownPaths.filter(isSubPage).sort();

  if (others.length === 0) {
    return null;
  }

  const titleFor = (path) => {
    const item = itemsByPath.get(path);
    return item?.title || item?.id || labelFromPath(path);
  };

  const tabs = [
    { label: 'Overview', href: overviewPath },
    ...others.map((path) => ({ label: titleFor(path), href: path })),
  ];

  return (
    <nav className="section-tabs" aria-label="Section pages">
      {tabs.map(({ label, href }) => (
        <UniversalLink
          key={href}
          href={href}
          className={`section-tabs__tab${
            currentPath === href ? ' section-tabs__tab--active' : ''
          }`}
        >
          {label}
        </UniversalLink>
      ))}
    </nav>
  );
};

SectionTabsListingView.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  data: PropTypes.object,
};

export default SectionTabsListingView;
